/**
 * Chakrikka product PDP: desktop (990px+) main gallery arrows stay enabled and
 * wrap by rotating the real slides at the edges, so the motion still feels like
 * a single step left/right instead of a long jump across the strip.
 */
(function () {
  const SECTION = 'section.section-chakrikka-product';
  const VIEWER = `${SECTION} slider-component[id^="GalleryViewer-"]`;
  const DESKTOP_MQ = '(min-width: 990px)';

  function getSlider(component) {
    return component.querySelector('[id^="Slider-"]');
  }

  function getSlides(slider) {
    return Array.from(slider.children).filter(
      (el) => el.classList.contains('product__media-item') && el.clientWidth > 0
    );
  }

  function getStep(slides) {
    if (slides.length < 2) return 0;
    return slides[1].offsetLeft - slides[0].offsetLeft;
  }

  function getCurrentIndex(slider, slides, step) {
    if (!slides.length || step <= 0) return 0;
    const raw = Math.round(slider.scrollLeft / step);
    return Math.min(slides.length - 1, Math.max(0, raw));
  }

  function clearDisabled(component, mq) {
    if (!mq.matches) return;
    component.querySelector('button[name="previous"]')?.removeAttribute('disabled');
    component.querySelector('button[name="next"]')?.removeAttribute('disabled');
  }

  function refreshSliderPages(component) {
    if (typeof component.resetPages === 'function') component.resetPages();
  }

  function scrollToIndex(slider, slides, index) {
    slider.scrollTo({
      left: slides[index].offsetLeft,
      behavior: 'smooth',
    });
  }

  function rotateForPrevious(component, slider) {
    const slides = getSlides(slider);
    const lastSlide = slides[slides.length - 1];
    if (!lastSlide) return false;

    slider.prepend(lastSlide);
    refreshSliderPages(component);

    const rotatedSlides = getSlides(slider);
    const step = getStep(rotatedSlides);
    if (step <= 0) return false;

    slider.scrollLeft = rotatedSlides[1].offsetLeft;
    requestAnimationFrame(() => {
      slider.scrollTo({ left: rotatedSlides[0].offsetLeft, behavior: 'smooth' });
    });
    return true;
  }

  function rotateForNext(component, slider) {
    const slides = getSlides(slider);
    const firstSlide = slides[0];
    if (!firstSlide) return false;

    slider.append(firstSlide);
    refreshSliderPages(component);

    const rotatedSlides = getSlides(slider);
    const step = getStep(rotatedSlides);
    const currentIndex = Math.max(0, rotatedSlides.length - 2);
    if (step <= 0 || !rotatedSlides[currentIndex]) return false;

    slider.scrollLeft = rotatedSlides[currentIndex].offsetLeft;
    requestAnimationFrame(() => {
      slider.scrollTo({ left: rotatedSlides[currentIndex + 1].offsetLeft, behavior: 'smooth' });
    });
    return true;
  }

  function handleCaptureClick(event, component, mq) {
    if (!mq.matches) return;

    const btn = event.target.closest('.slider-buttons button[name="previous"], .slider-buttons button[name="next"]');
    if (!btn || !component.contains(btn)) return;

    const slider = getSlider(component);
    if (!slider) return;

    const slides = getSlides(slider);
    if (slides.length < 2) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const step = getStep(slides);
    if (step <= 0) return;

    const currentIndex = getCurrentIndex(slider, slides, step);
    const isAtStart = currentIndex <= 0;
    const isAtEnd = currentIndex >= slides.length - 1;

    if (btn.name === 'previous' && isAtStart) {
      rotateForPrevious(component, slider);
      return;
    }

    if (btn.name === 'next' && isAtEnd) {
      rotateForNext(component, slider);
      return;
    }

    const targetIndex = btn.name === 'next' ? currentIndex + 1 : currentIndex - 1;
    scrollToIndex(slider, slides, targetIndex);
  }

  function bind(component) {
    if (component.dataset.chakrikkaDesktopLoop === 'true') return;
    component.dataset.chakrikkaDesktopLoop = 'true';

    const mq = window.matchMedia(DESKTOP_MQ);
    const slider = getSlider(component);

    const onScroll = () => clearDisabled(component, mq);
    if (slider) slider.addEventListener('scroll', onScroll, { passive: true });

    component.addEventListener('click', (event) => handleCaptureClick(event, component, mq), true);
    mq.addEventListener('change', onScroll);

    clearDisabled(component, mq);
    requestAnimationFrame(() => clearDisabled(component, mq));
    window.setTimeout(() => clearDisabled(component, mq), 100);
  }

  function init() {
    document.querySelectorAll(VIEWER).forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.('.chakrikka-product')) init();
  });
})();
